const EXCEL_EXTENSIONS = new Set(["xlsx", "xlsm", "xltx", "xltm", "xlsb"]);
const BROWSER_VIEWABLE_EXTENSIONS = new Set(["pdf", "doc", "docx"]);

/** `excel` opens in Excel for desktop, `browser` in a new tab, `null` not at all. */
export type ReportOpenMode = "excel" | "browser" | null;

const fileExtension = (filename: string) => {
	const dotIndex = filename.lastIndexOf(".");
	return dotIndex === -1 ? "" : filename.slice(dotIndex + 1).toLowerCase();
};

export const getReportOpenMode = (filename: string): ReportOpenMode => {
	const extension = fileExtension(filename);
	// Reports published before v7 are stored without an extension; they are
	// workbooks, and eox7's own report browser opens them in Excel too.
	if (extension === "" || EXCEL_EXTENSIONS.has(extension)) return "excel";
	if (BROWSER_VIEWABLE_EXTENSIONS.has(extension)) return "browser";
	return null;
};

const MOBILE_USER_AGENT = /Android|iPhone|iPad|iPod|Mobile/i;

type NavigatorWithUserAgentData = Navigator & {
	userAgentData?: { mobile?: boolean };
};

/** Desktop systems Microsoft ships no Excel for desktop on. Another app may still
 * claim the `ms-excel:` scheme there (LibreOffice does on Linux), so the link
 * would open the report without the add-in instead of failing. */
const NO_EXCEL_FOR_DESKTOP_USER_AGENT = /Linux|CrOS/;

const isMobileOrTablet = (nav: NavigatorWithUserAgentData): boolean => {
	if (nav.userAgentData?.mobile) return true;
	if (MOBILE_USER_AGENT.test(nav.userAgent)) return true;
	// iPadOS reports itself as a Mac, so a touch-capable "Mac" is a tablet.
	return /Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 0;
};

/** Only Excel for desktop on Windows or Mac can take an `ms-excel:` link with the add-in. */
export const lacksExcelForDesktop = (
	nav: NavigatorWithUserAgentData = navigator,
): boolean =>
	isMobileOrTablet(nav) || NO_EXCEL_FOR_DESKTOP_USER_AGENT.test(nav.userAgent);
